import React, { useState, useEffect, useContext } from "react";
import { API, graphqlOperation } from "aws-amplify";
import NumberFormat from "react-number-format";
import { makeStyles } from "@material-ui/core/styles";
import Container from "@material-ui/core/Container";
import Drawer from "@material-ui/core/Drawer";
import AppBar from "@material-ui/core/AppBar";
import Toolbar from "@material-ui/core/Toolbar";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import Typography from "@material-ui/core/Typography";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import Paper from "@material-ui/core/Paper";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemAvatar from "@material-ui/core/ListItemAvatar";
import ListItemText from "@material-ui/core/ListItemText";
import ListItemSecondaryAction from "@material-ui/core/ListItemSecondaryAction";
import CircularProgress from "@material-ui/core/CircularProgress";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import AddIcon from "@material-ui/icons/Add";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilter } from "@fortawesome/free-solid-svg-icons";
import logger from "../logger";
import { listProducts } from "../graphql/queries";
import { AppContext } from "../views/App";
import ImageLoader from "./ImageLoader";
import ProductoCreate from "./ProductoCreate";
import ProductoDetails from "./ProductoDetails";

const drawerWidth = "100vw";

const useStyles = makeStyles(theme => ({
  drawer: {
    width: drawerWidth,
    flexShrink: 0
  },
  drawerPaper: {
    width: drawerWidth,
    backgroundColor: "#fafafa"
  },
  backButton: {
    marginRight: theme.spacing(2)
  },
  addButton: {
    marginLeft: theme.spacing(2)
  },
  filterButton: {
    marginLeft: theme.spacing(2)
  },
  title: {
    flexGrow: 1
  },
  secondaryAction: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center"
  },
  loading: {
    minHeight: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#fafafa"
  },
  inicioPaper: {
    height: "calc(100vh - 56px)",
    [`${theme.breakpoints.up("xs")} and (orientation: landscape)`]: {
      height: "calc(100vh - 48px)"
    },
    [theme.breakpoints.up("sm")]: {
      height: "calc(100vh - 64px)"
    },
    backgroundColor: "#fafafa"
  },
  inicioContainer: {
    minHeight: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center"
  },
  inicioButton: {
    marginTop: theme.spacing(3)
  }
}));

export const fetchProductos = async (storeID, onStart, onEnd, onComplete) => {
  onStart();

  try {
    const productos = [];
    let next = null;

    do {
      const vars = {
        storeID: storeID,
        nextToken: next
      };

      const result = await API.graphql(graphqlOperation(listProducts, vars));

      if (result.data.productsByStore) {
        for (let i = 0; i < result.data.productsByStore.items.length; i++) {
          const producto = result.data.productsByStore.items[i];
          const body = JSON.parse(producto.body);

          productos.push({
            ...producto,
            body
          });
        }

        next = result.data.productsByStore.nextToken;
      }
    } while (Boolean(next));

    productos.sort((a, b) => {
      if (a.name > b.name) {
        return 1;
      }
      if (a.name < b.name) {
        return -1;
      }
      return 0;
    });

    onComplete(productos);
  } catch (err) {
    logger("ProductosList.fetchProductos.error", err);
  } finally {
    onEnd();
  }
};

export const SinProductos = ({ onClick }) => {
  const classes = useStyles();

  return (
    <Paper square className={classes.inicioPaper}>
      <Container maxWidth="md" className={classes.inicioContainer}>
        <Typography variant="h4">
          ¡Hola!{" "}
          <span role="img" aria-label=":wave:">
            👋
          </span>
        </Typography>

        <Typography variant="h5" align="center" color="textSecondary">
          Para empezar crea tu primer producto.
        </Typography>

        <Button
          className={classes.inicioButton}
          variant="contained"
          color="primary"
          size="large"
          onClick={onClick}
        >
          Crear producto
        </Button>
      </Container>
    </Paper>
  );
};

const ProductosList = ({ open, onClose }) => {
  const classes = useStyles();
  const { store, productosCount, onProductosCountChange } = useContext(AppContext);
  const [fetchingProductos, setFetchingProductos] = useState(true);
  const [productos, setProductos] = useState([]);
  const [producto, setProducto] = useState(null);
  const [lista, setLista] = useState([...productos]);
  const [filtros, setFiltros] = useState([]);
  const [filtro, setFiltro] = useState(0);
  const [anchorEl, setAnchorEl] = useState(null);
  const [openCreate, setOpenCreate] = useState(false);

  const openFilter = Boolean(anchorEl);

  useEffect(() => {
    if (store.id) {
      fetchProductos(
        store.id,
        () => {
          setFetchingProductos(true);
        },
        () => {
          setFetchingProductos(false);
        },
        productos => {
          onProductosCountChange(productos.length);
          setProductos(productos);
        }
      );
    }
  }, [store.id, onProductosCountChange]);

  useEffect(() => {
    const todos = {
      value: null,
      label: `Todos`,
      items: productos
    };

    const list = [todos];

    const inactivos = {
      value: 0,
      label: "Inactivos",
      items: productos.filter(p => !Boolean(p.status)) || []
    };

    list.push(inactivos);

    if (store.body.categories.length > 1) {
      for (let i = 0; i < store.body.categories.length; i++) {
        const item = store.body.categories[i];
        item.items =
          productos.filter(p => p.body.category === item.value && Boolean(p.status)) || [];

        list.push(item);
      }
    }

    setFiltros(list);
  }, [store.body.categories, productos]);

  useEffect(() => {
    if (filtro) {
      if (filtro === -1) {
        setLista([]);
      } else {
        setLista(filtros[filtro].items || []);
      }
    } else {
      setLista(productos);
    }
  }, [filtros, filtro, productos]);

  const openFilterMenu = event => {
    setAnchorEl(event.currentTarget);
  };

  const closeFilterMenu = () => {
    setAnchorEl(null);
  };

  const selectFilter = selected => {
    setFiltro(selected);
    closeFilterMenu();
  };

  const refresh = () => {
    fetchProductos(
      store.id,
      () => {
        setFetchingProductos(true);
      },
      () => {
        setFetchingProductos(false);
      },
      productos => {
        onProductosCountChange(productos.length);
        setProductos(productos);
      }
    );
  };

  return (
    <>
      <ProductoCreate open={openCreate} onCreated={refresh} onClose={() => setOpenCreate(false)} />
      <ProductoDetails producto={producto} onChange={refresh} onClose={() => setProducto(null)} />

      <Drawer
        className={classes.drawer}
        open={open}
        anchor="right"
        classes={{
          paper: classes.drawerPaper
        }}
      >
        <AppBar position="sticky">
          <Toolbar>
            <IconButton
              className={classes.backButton}
              edge="start"
              color="inherit"
              onClick={onClose}
            >
              <ArrowBackIcon />
            </IconButton>

            <Typography className={classes.title} variant="h6">
              Productos
            </Typography>

            <IconButton
              className={classes.addButton}
              edge="end"
              color="inherit"
              onClick={() => setOpenCreate(true)}
            >
              <AddIcon />
            </IconButton>

            <IconButton
              className={classes.filterButton}
              edge="end"
              color="inherit"
              onClick={openFilterMenu}
            >
              <FontAwesomeIcon icon={faFilter} size="xs" />
            </IconButton>
            <Menu anchorEl={anchorEl} open={openFilter} onClose={closeFilterMenu}>
              {filtros.map((f, i) => (
                <MenuItem key={`filtro-${i}`} onClick={() => selectFilter(i)}>
                  <ListItemText primary={f.label} />
                </MenuItem>
              ))}
            </Menu>
          </Toolbar>
        </AppBar>

        {productosCount === 0 ? (
          <SinProductos onClick={() => setOpenCreate(true)} />
        ) : fetchingProductos ? (
          <div className={classes.loading}>
            <CircularProgress />
          </div>
        ) : (
          <Paper square>
            <List disablePadding>
              {lista.map((producto, index) => (
                <ListItem
                  key={`item-${index}`}
                  button
                  divider
                  dense
                  alignItems="flex-start"
                  onClick={() => setProducto(producto)}
                >
                  {producto.medias.length > 0 && (
                    <ListItemAvatar>
                      <ImageLoader
                        avatar
                        thumb
                        variant="rounded"
                        imgKey={producto.medias[0].key}
                        alt={producto.name}
                      />
                    </ListItemAvatar>
                  )}
                  <ListItemText
                    primary={producto.name}
                    secondary={
                      (store.body.categories.find(c => c.value === producto.body.category) || {})
                        .label
                    }
                  />
                  <ListItemSecondaryAction onClick={() => setProducto(producto)}>
                    <div className={classes.secondaryAction}>
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        align="right"
                        className="mr-3"
                      >
                        <NumberFormat
                          value={producto.price}
                          thousandSeparator="."
                          decimalSeparator=","
                          prefix="$"
                          displayType={"text"}
                        />
                        <br />
                        {Boolean(producto.status) ? "Activo" : "Inactivo"}
                      </Typography>

                      <ChevronRightIcon color="primary" />
                    </div>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          </Paper>
        )}
      </Drawer>
    </>
  );
};

export default ProductosList;
